import { service } from '@ember/service'
import type RouterService from '@ember/routing/router-service'
import Component from '@glimmer/component'
import RouteTemplate from 'ember-route-template'
import type FastPhonicsReportingProgressReviewRoute from 'teacher-ui-client/reporting/ui-scope/fast-phonics/progress/review/route'
import { array } from '@ember/helper'
import { not } from 'ember-truth-helpers'
import { t } from 'ember-intl'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import isSubco from 'teacher-ui-client/helpers/is-subco'
import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { ReportingDetailedControls } from 'teacher-ui-client/components/reporting/detailed-controls'
import { ReportingNoDataMessageIf } from 'teacher-ui-client/components/reporting/no-data-message-if'
import TabGroup from 'teacher-ui-client/components/tab-group'
import ContentTile from 'teacher-ui-client/components/tiles/content'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import { StatBoxesTile } from 'teacher-ui-client/components/tiles/stat-boxes'
import PeaksProgress from 'teacher-ui-client/components/stat-boxes/peaks-progress'
import { TAB_TYPE_KEYS } from 'teacher-ui-client/components/tab-group/tab-types'

interface Signature {
  Args: { model: ModelFor<FastPhonicsReportingProgressReviewRoute> }
}

export class FastPhonicsProgressReviewPage extends Component<Signature> {
  @service router!: RouterService

  teachersRoute = 'reporting.ui-scope.fast-phonics.progress.review.teachers'

  studentsRoute = 'reporting.ui-scope.fast-phonics.progress.review.students'

  get isTeachersRoute() {
    return this.router.currentRouteName === this.teachersRoute
  }

  <template>
    <ReportingTeacherUiHeader @title={{t "reporting.fastPhonics.peaksProgress.review.pageTitle"}} />
    <ReportingDetailedControls
      @students={{@model.scopedStudents}}
      @exportOptions={{array ExportType.CSV ExportType.print}}
      @isDataPresent={{Boolean @model.stats.peaksProgress}}
      @showScopePicker={{not this.isTeachersRoute}}
      @showDatePicker={{true}}
      @studentRouteName="reporting.ui-scope.fast-phonics.progress.student.table"
    />
    <ReportingNoDataMessageIf @on={{not @model.stats.peaksProgress}}>
      <div class="text-center">
        <StatBoxesTile
          class="page-break-after-always inline-block"
          @title={{t "reporting.fastPhonics.peaksProgress.statBoxes.title"}}
          @tooltip={{t "reporting.fastPhonics.peaksProgress.statBoxes.tooltip"}}
        >
          {{#if @model.stats.peaksProgress}}
            <PeaksProgress @data={{@model.stats.peaksProgress.summary}} @scope={{@model.uiScope.scope}} />
          {{/if}}
        </StatBoxesTile>
      </div>

      <div class="mt-6">
        {{#if (isSubco)}}
          <TabGroup @theme={{themeKey "reading"}} @tabType={{TAB_TYPE_KEYS.shadow}} as |tabGroup|>
            <tabGroup.linkToTab @route={{this.teachersRoute}} data-test-fast-phonics-progress-teachers-link>
              {{t "reporting.fastPhonics.peaksProgress.review.tabs.teachers"}}
            </tabGroup.linkToTab>
            <tabGroup.linkToTab @route={{this.studentsRoute}} data-test-fast-phonics-progress-students-link>
              {{t "reporting.fastPhonics.peaksProgress.review.tabs.students"}}
            </tabGroup.linkToTab>
          </TabGroup>
        {{/if}}

        <ContentTile class="page-break-inside-auto relative print:border-none {{if (isSubco) 'rounded-tl-none'}}">
          <div class="overflow-x-auto print:overflow-x-hidden">
            {{outlet}}
          </div>
        </ContentTile>
      </div>
    </ReportingNoDataMessageIf>
  </template>
}

export default RouteTemplate(FastPhonicsProgressReviewPage)
